import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, UserCog } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { buildTeamApiService } from '../../services/buildTeamApi';
import {
  BuildTeamMember,
  TeamMemberRole,
  TeamMemberStatus,
  TEAM_MEMBER_ROLES,
  TEAM_MEMBER_STATUSES,
} from '../../types/buildTeam';

export interface EditTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  member: BuildTeamMember;
}

function formatStatusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function specialtiesToCommaDisplay(json: string | null): string {
  if (!json) return '';
  try {
    const arr = JSON.parse(json) as unknown;
    if (Array.isArray(arr)) {
      return arr.map(String).join(', ');
    }
    return json;
  } catch {
    return json;
  }
}

function specialtiesToJsonArray(commaSeparated: string): string | null {
  const parts = commaSeparated
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  return JSON.stringify(parts);
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

export function EditTeamMemberModal({
  isOpen,
  onClose,
  onSuccess,
  member,
}: EditTeamMemberModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [displayName, setDisplayName] = useState(member.display_name);
  const [role, setRole] = useState<TeamMemberRole>(member.role);
  const [status, setStatus] = useState<TeamMemberStatus>(member.status);
  const [specialtiesText, setSpecialtiesText] = useState(specialtiesToCommaDisplay(member.specialties));
  const [notes, setNotes] = useState(member.notes ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDisplayName(member.display_name);
    setRole(member.role);
    setStatus(member.status);
    setSpecialtiesText(specialtiesToCommaDisplay(member.specialties));
    setNotes(member.notes ?? '');
    setError(null);
  }, [isOpen, member]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await buildTeamApiService.updateTeamMember(member.id, {
        display_name: displayName.trim(),
        role,
        status,
        specialties: specialtiesToJsonArray(specialtiesText),
        notes: notes.trim() || null,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-member-title"
      >
        <div className="modal-header">
          <h2 id="edit-member-title" className="modal-title flex items-center gap-2">
            <UserCog className="w-5 h-5 text-accent-300" />
            Edit team member
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="modal-close touch-manipulation"
            aria-label="Close"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={LABEL_CLASS}>Minecraft UUID</label>
            <input
              type="text"
              value={member.minecraft_uuid}
              readOnly
              disabled
              className="glass-input w-full font-mono text-xs"
            />
            <p className="text-xs text-glass-500 mt-1">UUID cannot be changed after creation.</p>
          </div>

          <div>
            <label className={LABEL_CLASS}>Display name *</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="glass-input w-full"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as TeamMemberRole)}
                className="glass-select w-full"
              >
                {TEAM_MEMBER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {formatStatusLabel(r)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={LABEL_CLASS}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as TeamMemberStatus)}
                className="glass-select w-full"
              >
                {TEAM_MEMBER_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatStatusLabel(s)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Specialties</label>
            <input
              type="text"
              value={specialtiesText}
              onChange={(e) => setSpecialtiesText(e.target.value)}
              className="glass-input w-full"
              placeholder="Comma-separated"
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="glass-input w-full"
              rows={3}
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="glass-button-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
